/**
 * Auth module — manages JWT token and role in localStorage.
 * Web-only replacement for the Expo SecureStore-based mobile auth.
 */

const TOKEN_KEY = 'auth_token'
const ROLE_KEY = 'auth_role'

// ---------------------------------------------------------------------------
// Token + role storage
// ---------------------------------------------------------------------------

export function saveAuth(token: string, role: 'admin' | 'viewer'): void {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(ROLE_KEY, role)
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function getRole(): 'admin' | 'viewer' | null {
  return localStorage.getItem(ROLE_KEY) as 'admin' | 'viewer' | null
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(ROLE_KEY)
}

// ---------------------------------------------------------------------------
// Token validity
// ---------------------------------------------------------------------------

interface JwtPayload {
  sub?: string
  role?: 'admin' | 'viewer'
  exp?: number
}

/**
 * Decodes a JWT payload without verifying its signature.
 * Client-side only — used to detect expiry, never for trust decisions.
 */
function decodeToken(token: string): JwtPayload | null {
  try {
    const segment = token.split('.')[1]
    if (!segment) return null
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json) as JwtPayload
  } catch {
    return null
  }
}

/**
 * Returns true only if a token exists AND has not expired.
 * Prevents the auth guard from admitting users with a stale token.
 */
export function isTokenValid(): boolean {
  const token = getToken()
  if (!token) return false
  const payload = decodeToken(token)
  if (!payload || typeof payload.exp !== 'number') return false
  // `exp` is in seconds; compare against the current time in ms.
  return payload.exp * 1000 > Date.now()
}

// ---------------------------------------------------------------------------
// Auth state (module-level, consumed by AuthContext)
// ---------------------------------------------------------------------------

let _isAuthenticated: boolean = isTokenValid()
let _authListeners: Array<(authed: boolean) => void> = []

export function isAuthenticated(): boolean {
  return _isAuthenticated
}

export function setAuthenticated(value: boolean): void {
  _isAuthenticated = value
  _authListeners.forEach((fn) => fn(value))
}

/**
 * Clears stored credentials and notifies listeners that the session ended.
 * Invoked when the API returns 401 (expired or invalid token) so the app
 * can redirect to the login screen instead of silently failing.
 */
export function handleUnauthorized(): void {
  clearAuth()
  setAuthenticated(false)
}

export function onAuthChange(listener: (authed: boolean) => void): () => void {
  _authListeners.push(listener)
  return () => {
    _authListeners = _authListeners.filter((fn) => fn !== listener)
  }
}
